import React from 'react';
import { Package, Sparkles } from 'lucide-react';

const products = [
  {
    id: 1,
    name: 'Dry Turmeric',
    description: 'Premium quality whole turmeric, naturally dried and sourced from the finest farms.',
    image: '/dry-turmeric.png',
    featured: true,
  },
  {
    id: 2,
    name: 'Dry Red Chilli',
    description: 'Vibrant, aromatic red chillies with consistent heat and rich color.',
    image: '/dry-red-chilli.png',
    featured: false,
  },
  {
    id: 3,
    name: 'Turmeric Powder',
    description: 'Finely ground turmeric powder with high curcumin content and golden color.',
    image: '/turmeric-powder.png',
    featured: false,
  },
  {
    id: 4,
    name: 'Red Chilli Powder',
    description: 'Pure chilli powder with intense flavor and color, perfect for export markets.',
    image: '/red-chilli-powder.png',
    featured: false,
  },
  {
    id: 5,
    name: 'Jaggery Powder',
    description: 'Natural, unrefined sweetener with rich nutritional value and authentic taste.',
    image: '/jaggery-powder.png',
    featured: false,
  },
];

export const ExportDivision = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section
      id="export"
      className="py-24 md:py-32 px-6 md:px-12 bg-navy relative overflow-hidden"
      data-testid="export-section"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-pattern opacity-50" />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 border border-gold/30 rounded-full mb-6">
            <Package size={20} className="text-gold" />
            <span className="text-gold uppercase tracking-widest text-sm font-semibold">Export Division</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6">
            Premium <span className="text-gold-gradient">Agricultural Products</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Sourcing and exporting the finest quality spices and natural products from India to global markets.
          </p>
          <div className="section-divider mt-8" />
        </div>

        {/* Bento Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Featured Product - Takes 2 columns on large screens */}
          <div
            className="lg:col-span-2 lg:row-span-2 product-card group"
            data-testid={`product-card-${products[0].id}`}
          >
            <div className="relative h-full overflow-hidden">
              <img
                src={products[0].image}
                alt={products[0].name}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/50 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles size={20} className="text-gold" />
                  <span className="text-gold uppercase tracking-widest text-xs font-semibold">Featured</span>
                </div>
                <h3 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
                  {products[0].name}
                </h3>
                <p className="text-gray-300 mb-6 leading-relaxed">{products[0].description}</p>
                <button
                  onClick={scrollToContact}
                  className="btn-gold"
                  data-testid={`inquire-btn-${products[0].id}`}
                >
                  Inquire Now
                </button>
              </div>
            </div>
          </div>

          {/* Other Products */}
          {products.slice(1).map((product) => (
            <div
              key={product.id}
              className="product-card group"
              data-testid={`product-card-${product.id}`}
            >
              <div className="relative h-full overflow-hidden">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/60 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-xl md:text-2xl font-heading font-bold text-white mb-3">
                    {product.name}
                  </h3>
                  <p className="text-gray-300 text-sm mb-4 leading-relaxed">{product.description}</p>
                  <button
                    onClick={scrollToContact}
                    className="text-gold font-semibold text-sm uppercase tracking-wider hover:text-gold-light transition-colors flex items-center gap-2 group"
                    data-testid={`inquire-btn-${product.id}`}
                  >
                    Inquire
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Export Certifications - Updated */}
        <div className="mt-16 glass-panel p-8 rounded-lg">
          <h3 className="text-2xl font-heading font-semibold text-gold mb-6 text-center">
            Export Certifications & Registrations
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center mb-8">
            <div>
              <div className="text-silver font-semibold mb-2">UDYAM</div>
              <div className="text-gray-400 text-sm">Registered</div>
            </div>
            <div>
              <div className="text-silver font-semibold mb-2">SHOP ACT</div>
              <div className="text-gray-400 text-sm">Licensed</div>
            </div>
            <div>
              <div className="text-silver font-semibold mb-2">GST</div>
              <div className="text-gray-400 text-sm">Registered</div>
            </div>
            <div>
              <div className="text-silver font-semibold mb-2">IEC</div>
              <div className="text-gray-400 text-sm">Certified</div>
            </div>
          </div>
          
          {/* Download Brochure Button */}
          <div className="text-center">
            <a
              href="/agatsya-brochure.pdf"
              download
              className="inline-flex items-center gap-3 btn-gold"
              data-testid="export-brochure-btn"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
              Download Product & Services Brochure
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
